import { useContext, useState } from "react";
import TextField from "@mui/material/TextField";
import { SoecoButton } from "../../components/SoecoButton";
import { Loader } from "../../components/Loader";
import {
    ActionButtons,
    CompanyNameWrapper,
    Form,
    FormWrapper,
    NewInventoryPageWrapper,
} from "./NewInventory.styled";
import { EmployeeContext } from "../../context/EmployeeContext";
import { useNavigate, useParams } from "react-router-dom";
import { useGetCompany } from "../../hooks/companyHooks";
import { CreateInventory, useAddInventory } from "../../hooks/inventoryHooks";
import { SoecoHeader } from "../../components/SoecoHeader";
import { ErrorRecord } from "../../types";
import { useSnackBar } from "../../context/SnackbarContext";
import { Checkbox, Divider, FormControlLabel, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { getRolePriority } from "../../utils/util";
import AddEmailDialog from "../../components/AddEmailDialog";
import { AdminCompanyFilter } from "../../components/AdminCompanyFilter";
import { sx } from "../../utils/util";

export const NewInventory = () => {
    const { companyId } = useParams();
    const mutation = useAddInventory();
    const { employee } = useContext(EmployeeContext);
    const companyQuery = useGetCompany(companyId ?? employee.companyId);
    const [error, setError] = useState<boolean>(false);
    const [errors, setErrors] = useState<ErrorRecord<CreateInventory>>({});
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const today = new Date().toLocaleDateString("sv-SE"); // YYYY-MM-DD
    const [isAddEmailDialogOpen, setIsAddEmailDialogOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");

    const [currentCompanyId, setCurrentCompanyId] = useState(companyId ?? employee.companyId);

    const [form, setForm] = useState<CreateInventory>({
        companyId: currentCompanyId ?? "",
        name: "",
        project: "",
        estimatedDeliveryDate: today,
        customerId: "",
        description: "",
        emailaddresses: [],
    });

    const snackBar = useSnackBar();
    const navigate = useNavigate();

    const isValidForm = () => {
        let valid = true;
        const newErrors: ErrorRecord<CreateInventory> = {};
        if (form.name.length < 1) {
            valid = false;
            newErrors.name = "Name cannot be empty";
        }
        if (
            form.estimatedDeliveryDate &&
            form.estimatedDeliveryDate.match(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/) ==
                null
        ) {
            valid = false;
            newErrors.estimatedDeliveryDate = "Date must be written as YYYY-MM-DD";
        }
        if (form.estimatedDeliveryDate === "") {
            form.estimatedDeliveryDate = undefined;
        }
        if (!valid) {
            setErrors(newErrors);
            setError(true);
            setDisableButton(true);
            return false;
        } else {
            setErrors({});
            setError(false);
            return true;
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setDisableButton(false);
        setError(false);
    };

    const handleSubmit = () => {
        if (isValidForm()) {
            if (!currentCompanyId || !employee.companyId) {
                return;
            }
            mutation.mutate(
                { companyId: currentCompanyId, inventory: form },
                {
                    onError: error => {
                        if (error instanceof Error) {
                            snackBar.showSnackBar(error.message, "error");
                            setDisableButton(true);
                        }
                    },
                    onSuccess: data => {
                        snackBar.showSnackBar("Inventory Created", "success");
                        navigate(`/companies/${currentCompanyId}/inventories/${data.id}`);
                    },
                },
            );
        }
    };
    const handleChangeEmails = (email: string) => {
        const existingEmailIndex = form.emailaddresses?.findIndex(
            existingEmail => existingEmail === email,
        );
        if (existingEmailIndex !== -1) {
            setForm({
                ...form,
                emailaddresses: form.emailaddresses?.filter(
                    (existingEmail, index) => index !== existingEmailIndex,
                ),
            });
        } else {
            setForm({
                ...form,
                emailaddresses: [...(form.emailaddresses || []), email],
            });
        }
        setDisableButton(false);
        setError(false);
    };
    const handleAddEmailClick = () => {
        setIsAddEmailDialogOpen(true);
    };

    const handleCloseAddEmailDialog = () => {
        setIsAddEmailDialogOpen(false);
        setNewEmail("");
    };

    const handleSaveNewEmail = () => {
        if (newEmail.trim() === "") return;
        setForm(prev => ({
            ...prev,
            emailaddresses: [...(prev.emailaddresses || []), newEmail.trim()],
        }));

        handleCloseAddEmailDialog();
    };

    const onChange = (newId: string) => {
        setCurrentCompanyId(newId);
        setForm({ ...form, ["companyId"]: newId });
    };

    return (
        <NewInventoryPageWrapper>
            {companyQuery.isLoading || companyQuery.data == undefined ? (
                <>
                    <Loader />
                </>
            ) : companyQuery.isError ? (
                <>
                    <p>Error</p>
                </>
            ) : (
                <>
                    <FormWrapper>
                        <SoecoHeader>Create inventory</SoecoHeader>

                        <CompanyNameWrapper alignItems="flex-start">
                            {employee.role === "admin" ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "8px",
                                        marginTop: "10%",
                                    }}
                                >
                                    <Typography
                                        color="#8c8c8c"
                                        sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: "600",
                                            fontSize: "18",
                                        }}
                                    >
                                        Company:{" "}
                                    </Typography>
                                    <Typography
                                        color="black"
                                        sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: "600",
                                            fontSize: "18",
                                        }}
                                    >
                                        {companyQuery.data.name}
                                    </Typography>
                                </div>
                            ) : (
                                <AdminCompanyFilter
                                    currentCompanyId={currentCompanyId}
                                    onChange={onChange}
                                    width="100%"
                                />
                            )}
                        </CompanyNameWrapper>
                        <Form noValidate autoComplete="off">
                            <TextField
                                label="Inventory name"
                                name="name"
                                onChange={handleChange}
                                variant="outlined"
                                required
                                error={error && errors.name != undefined}
                                helperText={error && errors.name}
                                sx={sx}
                            />
                            <TextField
                                label="Project"
                                name="project"
                                onChange={handleChange}
                                variant="outlined"
                                sx={sx}
                            />
                            <TextField
                                label="Customer"
                                name="customerId"
                                onChange={handleChange}
                                variant="outlined"
                                sx={sx}
                            />
                            <TextField
                                label="Inventory date"
                                name="estimatedDeliveryDate"
                                onChange={handleChange}
                                type="text"
                                inputProps={{
                                    "aria-label": `Inventory date, date selection, ${
                                        form.estimatedDeliveryDate || "No date selected"
                                    }`,
                                }}
                                value={form.estimatedDeliveryDate}
                                variant="outlined"
                                error={error && errors.estimatedDeliveryDate != undefined}
                                helperText={error && errors.estimatedDeliveryDate}
                                sx={sx}
                            />
                            {getRolePriority(employee.role) === 0 && (
                                <>
                                    <Divider />
                                    <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
                                        <legend style={{ fontWeight: 600, marginBottom: "8px" }}>
                                            Email Recievers
                                        </legend>
                                        {form?.emailaddresses?.map(
                                            (email: string, index: number) => (
                                                <FormControlLabel
                                                    key={index}
                                                    control={
                                                        <Checkbox
                                                            onChange={() =>
                                                                handleChangeEmails(email)
                                                            }
                                                            checked={
                                                                form.emailaddresses?.includes(
                                                                    email,
                                                                ) || false
                                                            }
                                                        />
                                                    }
                                                    label={email}
                                                />
                                            ),
                                        )}
                                    </fieldset>

                                    {form.emailaddresses?.map((email, index) => {
                                        if (!companyQuery.data.emailaddresses?.includes(email)) {
                                            return (
                                                <FormControlLabel
                                                    key={index}
                                                    control={
                                                        <Checkbox
                                                            onChange={() =>
                                                                handleChangeEmails(email)
                                                            }
                                                            checked={
                                                                form.emailaddresses?.includes(
                                                                    email,
                                                                ) || false
                                                            }
                                                        />
                                                    }
                                                    label={email}
                                                />
                                            );
                                        }
                                    })}
                                    <AddEmailDialog
                                        isAddEmailDialogOpen={isAddEmailDialogOpen}
                                        handleCloseAddEmailDialog={handleCloseAddEmailDialog}
                                        newEmail={newEmail}
                                        setNewEmail={setNewEmail}
                                        handleSaveNewEmail={handleSaveNewEmail}
                                    />
                                    <IconButton
                                        onClick={handleAddEmailClick}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignSelf: "flex-start",
                                        }}
                                        aria-label="add new reciever email"
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </>
                            )}

                            <TextField
                                label="Description"
                                name="description"
                                onChange={handleChange}
                                rows={4}
                                multiline
                                sx={{
                                    ...sx,
                                    marginBottom: "90px",
                                }}
                            />
                        </Form>
                    </FormWrapper>
                </>
            )}
            <ActionButtons>
                <SoecoButton fullWidth size="large" color="warning" onClick={() => navigate(-1)}>
                    Back
                </SoecoButton>
                <SoecoButton
                    fullWidth
                    size="large"
                    type="submit"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={mutation.isPending || disableButton}
                >
                    Submit
                </SoecoButton>
            </ActionButtons>
        </NewInventoryPageWrapper>
    );
};
